/**
 * Take care importing code here! This is injected into every app as an individual script tag.
 */
import { getTrellisTestDayId, isTrellisTestDay, setupLocalStorageForTestDay } from './trellis-test-day';
import { getHostname, getPathname, getQueryParams, isWebdriver } from './utils';
const QA_APP_REGEXP = /(app|local)(-[a-z]+[0-9])?\.hubspotqa\.com/;
const PORTAL_ID_REGEXP = /^\/(?:[A-Za-z0-9-_]*)\/(\d+)(?:\/|$)/;
const VALID_THEMES = new Set(['trellis-light', 'canvas-light']);
export const DEFAULT_THEME = 'canvas-light';
const COOKIE_THEME_KEY = 'hubspot-theme';
const DATASET_THEME_KEY = 'hubspotTheme';

/**
 * This is a different key than the one used in the base theme switcher.
 * The base theme switcher uses 'trellis:theme', while this one uses 'hubspot-theme'.
 * This is intentional to avoid conflicts with the base theme switcher, since this
 * key is read by the `setup-theme.ts` file which is used to set _customer_ themes in specific apps,
 * while the `trellis:theme` key is used internally to set the default theme app-wide
 */
export const LOCAL_STORAGE_THEME_KEY = 'hubspot-theme';
export const HUBLESS_LOCAL_STORAGE_THEME_KEY = 'hubspot-theme:hubless';
function getCookie(name) {
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
  return match ? match[2] : null;
}
export function getPortalId() {
  const pathname = getPathname();
  const match = pathname.match(PORTAL_ID_REGEXP);
  return match ? match[1] : null;
}
function getPortalScopedThemeSettings() {
  try {
    const themeString = localStorage.getItem(LOCAL_STORAGE_THEME_KEY);
    if (themeString) {
      return JSON.parse(themeString);
    }
  } catch (e) {
    // do nothing
  }
  return {};
}
function getLocalStorageTheme() {
  try {
    const portalId = getPortalId();
    if (!portalId) {
      // the hubless theme is just the theme name
      const hublessTheme = localStorage.getItem(HUBLESS_LOCAL_STORAGE_THEME_KEY);
      return hublessTheme;
    }
    const themeSettings = getPortalScopedThemeSettings();
    if (portalId in themeSettings) {
      return themeSettings[portalId];
    }
  } catch (e) {
    // do nothing
  }
  return null;
}
export function getThemeSetting() {
  try {
    // get theme setting from hubspot-theme cookie with higher priority than localStorage
    const themeCookie = getCookie(COOKIE_THEME_KEY);
    const localStorageTheme = getLocalStorageTheme();
    const themeSetting = themeCookie || localStorageTheme;
    if (themeSetting && VALID_THEMES.has(themeSetting)) {
      return themeSetting;
    }
  } catch (e) {
    // do nothing
  }
  return null;
}

/**
 * In the signup -> setup flow, there is no way to communicate between the hubless and hubful
 * UIs which theme to load initially. We cannot set local storage because the hubful setup app
 * may be on a hubletized domain (-eu1, -ap1), but the hubless signup app is na1-only. As such,
 * when signup redirects to setup, it will pass __initialHubspotTheme which we will use to
 * set the theme in localStorage before we try to read it so that there's an initial theme
 * that will be sticky for future page loads
 */
export function maybeSetInitialTheme() {
  const hasThemeSetting = getThemeSetting();
  if (hasThemeSetting) {
    return;
  }
  const queryParams = getQueryParams();
  const initialTheme = queryParams.__initialHubspotTheme;
  const portalId = getPortalId();
  if (portalId && initialTheme && VALID_THEMES.has(initialTheme)) {
    try {
      const themeSettings = getPortalScopedThemeSettings();
      const newThemeSettings = Object.assign({}, themeSettings, {
        [portalId]: initialTheme
      });
      localStorage.setItem(LOCAL_STORAGE_THEME_KEY, JSON.stringify(newThemeSettings));
    } catch (_unused) {
      // do nothing, let them receive the default theme if we couldn't set it
    }
  }
}
const getBuildTimeTheme = () => {
  const theme = window.__FORCE_HUBSPOT_THEME__;
  if (theme && VALID_THEMES.has(theme)) {
    return theme;
  }
  return undefined;
};
export function isHubspotQA() {
  const hostname = getHostname();
  return QA_APP_REGEXP.test(hostname);
}

/**
 * This function is run in the `hubspot-theme` file which is loaded in the <head /> of apps.
 * This is used to synchronize a users theme setting (cookie/local storage) with the data-hubspot-theme
 * `<html />` tag of the app, which is what ThemeProvider uses to derive the theme, and what the CSS
 * uses to select the correct values for the CSS variables. This code runs very early on the page,
 * and is inlined into every app.
 */
export function setupTheme() {
  try {
    maybeSetInitialTheme();
    const isQa = isHubspotQA();
    let defaultTheme = DEFAULT_THEME;

    // Trellis Test Days are only applied in QA.
    if (isQa) {
      const now = new Date();
      const dateInput = {
        day: now.getUTCDay(),
        hour: now.getUTCHours(),
        now: now.getTime()
      };

      // We don't want to apply TTD code to ATs or unit tests
      // ATs will have VR snapshots that rely on Canvas Light theme
      // Unit tests might be relying on style-specific behaviour for their selectors.
      const isTestDay = isTrellisTestDay() && !isWebdriver();
      if (isTestDay) {
        const testDayId = getTrellisTestDayId(dateInput);
        setupLocalStorageForTestDay(String(testDayId));
        defaultTheme = 'trellis-light';
      }
    }
    const themeSetting = getBuildTimeTheme() || getThemeSetting() || defaultTheme;
    document.documentElement.dataset[DATASET_THEME_KEY] = themeSetting;
  } catch (_unused2) {
    // do nothing
  }
}